import React, { useState, useEffect } from 'react';
import './ReportsListView.css';

/**
 * Компонент для отображения списка налоговых отчётов
 * с группировкой по годам и статусами
 */
export function ReportsListView({ months, onViewMonth, onOpenProgressPanel, onDeleteMonth, onDeleteAll }) {
  const [expandedYears, setExpandedYears] = useState(new Set());
  const [groupedMonths, setGroupedMonths] = useState({});

  useEffect(() => {
    // Группируем месяцы по годам
    const grouped = {};
    months.forEach(month => {
      const year = month.substring(0, 4);
      if (!grouped[year]) {
        grouped[year] = [];
      }
      grouped[year].push(month);
    });

    // Сортируем года по убыванию
    const sorted = {};
    Object.keys(grouped).sort((a, b) => b - a).forEach(year => {
      sorted[year] = grouped[year].sort((a, b) => b.localeCompare(a));
    });

    setGroupedMonths(sorted);

    // Автоматически раскрываем текущий год
    const currentYear = new Date().getFullYear().toString();
    if (sorted[currentYear]) {
      setExpandedYears(new Set([currentYear]));
    }
  }, [months]);

  const toggleYear = (year) => {
    const newExpanded = new Set(expandedYears);
    if (newExpanded.has(year)) {
      newExpanded.delete(year);
    } else {
      newExpanded.add(year);
    }
    setExpandedYears(newExpanded);
  };

  const expandAll = () => {
    setExpandedYears(new Set(Object.keys(groupedMonths)));
  };

  const collapseAll = () => {
    setExpandedYears(new Set());
  };

  const years = Object.keys(groupedMonths);

  if (years.length === 0) {
    return (
      <div className="reports-list-empty">
        <h3>Нет загруженных отчётов</h3>
        <p>Начните загрузку отчётов, нажав кнопку ниже</p>
        <button
          className="btn-primary-large"
          onClick={onOpenProgressPanel}
        >
          Загрузить отчёты
        </button>
      </div>
    );
  }

  return (
    <div className="reports-list-view">
      {/* Заголовок и кнопка загрузки */}
      <div className="reports-list-header">
        <div className="reports-list-title">
          <h3>Загруженные отчёты</h3>
          <span className="reports-count">{months.length} периодов</span>
        </div>
        <div className="reports-list-actions">
          <button className="btn-link" onClick={expandAll}>
            Развернуть все
          </button>
          <button className="btn-link" onClick={collapseAll}>
            Свернуть все
          </button>
          <button
            className="btn-danger"
            onClick={() => {
              if (window.confirm('Удалить все OZON отчёты? Это действие нельзя отменить.')) {
                onDeleteAll();
              }
            }}
            style={{ marginRight: '8px' }}
          >
            Удалить всё
          </button>
          <button
            className="btn-primary"
            onClick={onOpenProgressPanel}
          >
            Загрузить отчёты
          </button>
        </div>
      </div>

      {/* Список по годам */}
      <div className="reports-years-list">
        {years.map(year => {
          const isExpanded = expandedYears.has(year);
          const yearMonths = groupedMonths[year];

          return (
            <div key={year} className="year-group">
              <div
                className="year-header"
                onClick={() => toggleYear(year)}
              >
                <span className="year-toggle">
                  {isExpanded ? '▼' : '▶'}
                </span>
                <span className="year-label">{year}</span>
                <span className="year-badge">{yearMonths.length} мес.</span>
              </div>

              {isExpanded && (
                <div className="year-months">
                  <table className="months-table">
                    <thead>
                      <tr>
                        <th>Период</th>
                        <th>Месяц</th>
                        <th>Статус</th>
                        <th>Действия</th>
                      </tr>
                    </thead>
                    <tbody>
                      {yearMonths.map(month => {
                        const monthName = getMonthName(month);
                        return (
                          <tr key={month} className="month-row">
                            <td className="month-period">{month}</td>
                            <td className="month-name">{monthName}</td>
                            <td>
                              <span className="status-badge status-loaded">
                                Загружен
                              </span>
                            </td>
                            <td>
                              <button
                                className="btn-view"
                                onClick={() => onViewMonth(month)}
                                style={{ marginRight: '8px' }}
                              >
                                Просмотр
                              </button>
                              <button
                                className="btn-danger-small"
                                onClick={() => {
                                  if (window.confirm(`Удалить отчёт за ${monthName} (${month})?`)) {
                                    onDeleteMonth(month);
                                  }
                                }}
                              >
                                Удалить
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function getMonthName(period) {
  const monthNames = [
    'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
    'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'
  ];

  const [year, month] = period.split('-');
  const monthIndex = parseInt(month, 10) - 1;

  return monthNames[monthIndex] || month;
}
